import React from 'react';
import { Building2, Check, MapPin, X } from 'lucide-react';
import type { MedicoClinicaVinculadaContract } from '@sstexames/shared-types';

import { formatCnpj } from '../utils/formatters';

interface ClinicaSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  clinicas: MedicoClinicaVinculadaContract[];
  selectedClinicaId: number | null;
  onSelectClinica: (clinica: MedicoClinicaVinculadaContract) => void;
}

export default function ClinicaSelectorModal({
  isOpen,
  onClose,
  clinicas,
  selectedClinicaId,
  onSelectClinica,
}: ClinicaSelectorModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-100 flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Building2 className="w-5 h-5 text-primary-200" />
            <h3 className="text-lg font-bold">Selecionar Clínica de Atendimento</h3>
          </div>
          {selectedClinicaId && (
            <button
              onClick={onClose}
              className="p-1.5 rounded-full hover:bg-white/20 transition-colors text-white cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto space-y-4">
          <p className="text-sm text-slate-600">
            Você está vinculado às seguintes clínicas que atendem pelo sistema. Selecione em qual
            unidade você está realizando atendimentos:
          </p>

          <div className="space-y-2.5">
            {clinicas.map((clinica) => {
              const isSelected = selectedClinicaId === clinica.id;
              return (
                <button
                  key={clinica.id}
                  onClick={() => {
                    onSelectClinica(clinica);
                    onClose();
                  }}
                  className={`w-full text-left p-4 rounded-xl border transition-all flex items-center justify-between group cursor-pointer ${
                    isSelected
                      ? 'border-primary-500 bg-primary-50/70 shadow-sm'
                      : 'border-slate-200 hover:border-primary-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="space-y-1">
                    <div className="font-semibold text-slate-800 text-base group-hover:text-primary-700 transition-colors">
                      {clinica.nome}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-2">
                      <span>CNPJ: {formatCnpj(clinica.cnpj)}</span>
                      {clinica.cidade && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-slate-400" />
                          {clinica.cidade}/{clinica.uf}
                        </span>
                      )}
                    </div>
                  </div>

                  {isSelected && (
                    <div className="w-6 h-6 rounded-full bg-primary-600 text-white flex items-center justify-center shrink-0">
                      <Check className="w-4 h-4" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {selectedClinicaId && (
          <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-primary-700 bg-white border border-primary-300 hover:bg-primary-50 hover:border-primary-400 rounded-xl transition-colors cursor-pointer"
            >
              Fechar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
